import { toast, Bounce } from "react-toastify"
import {
  isNetworkError,
  NETWORK_ERROR_MESSAGE,
  toastNetworkError,
} from "./networkError"

export const SERVER_ERROR_TOAST_ID = "server-error"
export const SERVER_ERROR_MESSAGE =
  "Something went wrong. Please try again."

const DEFAULT_TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const SERVER_TOAST_OPTIONS = {
  ...DEFAULT_TOAST_OPTIONS,
  toastId: SERVER_ERROR_TOAST_ID,
}

const joinMessages = messages =>
  messages
    .flat()
    .filter(message => typeof message === "string" && message.trim())
    .join(" ")

const getErrorStatus = error =>
  Number(error?.response?.status || error?.status) || null

export const isServerError = error => {
  const status = getErrorStatus(error)
  return status >= 500
}

/**
 * Single shared toast for 5xx / server failures (same idea as network toast).
 * Repeated outages won't stack multiple toasts.
 */
export const toastServerError = (message = SERVER_ERROR_MESSAGE) => {
  const text =
    typeof message === "string" && message.trim()
      ? message.trim()
      : SERVER_ERROR_MESSAGE
  toast.error(text, SERVER_TOAST_OPTIONS)
  return text
}

export const dismissServerErrorToast = () => {
  toast.dismiss(SERVER_ERROR_TOAST_ID)
}

/**
 * Reads an Axios (or Axios-like) error and returns a single user-facing message.
 * Prefers `data.errors` (array, object of arrays, or string); falls back to `data.message`.
 */
export const getApiErrorMessage = (
  error,
  fallback = "Something went wrong",
) => {
  if (isNetworkError(error)) {
    return NETWORK_ERROR_MESSAGE
  }

  const data = error?.response?.data || error?.data || error
  const apiErrors = data?.errors

  if (Array.isArray(apiErrors)) {
    const combined = joinMessages(apiErrors)
    if (combined) return combined
  } else if (apiErrors && typeof apiErrors === "object") {
    const combined = joinMessages(Object.values(apiErrors))
    if (combined) return combined
  } else if (typeof apiErrors === "string" && apiErrors.trim()) {
    return apiErrors
  }

  if (typeof data?.message === "string" && data.message.trim()) {
    return data.message
  }

  if (
    typeof error?.message === "string" &&
    error.message.trim() &&
    !error.isAxiosError
  ) {
    return error.message
  }

  if (isServerError(error)) {
    return SERVER_ERROR_MESSAGE
  }

  return fallback
}

export const toastApiError = (error, fallback) => {
  // `api/axios` already toasts on failure and returns null unless skipErrorToast.
  // Calling toastApiError(null, fallback) would show a second, generic toast.
  if (error == null) {
    return typeof fallback === "string" ? fallback : ""
  }

  if (isNetworkError(error)) {
    return toastNetworkError()
  }

  const message = getApiErrorMessage(error, fallback)
  if (isServerError(error)) {
    return toastServerError(message)
  }

  toast.error(message, DEFAULT_TOAST_OPTIONS)
  return message
}
